import { useMemo, useState } from 'react';
import {
  DRONE_NDVI_RANGE,
  DRONE_VEGETATION_CLASSES,
  ndviToEstimatedYieldTonsHa,
  vegetationClassMidpointNdvi,
  type DroneMissionConfig,
} from '../constants/dronePrescriptionLayers';
import type { DronePrescriptionState } from '../utils/dronePrescriptionAnalytics';
import { useTheme } from '../theme/ThemeContext';

type DronePrescriptionPanelProps = {
  state: DronePrescriptionState;
  mission: DroneMissionConfig | null;
  usingMissionExtent: boolean;
  variant?: 'inline' | 'overlay';
  onClose?: () => void;
};

function fmtNum(n: number, digits = 3): string {
  if (Number.isNaN(n)) return '—';
  return n.toFixed(digits);
}

function fmtHa(n: number): string {
  if (Number.isNaN(n)) return '—';
  return n.toFixed(3);
}

function classColorForNdvi(value: number): string {
  const cls = DRONE_VEGETATION_CLASSES.find(
    (c) => value >= c.min && value <= c.max
  );
  return cls?.color ?? '#9ca3af';
}

function DroneMetadataFooter({
  mission,
  isDark,
}: {
  mission: DroneMissionConfig;
  isDark: boolean;
}) {
  const muted = isDark ? 'text-gray-500' : 'text-gray-400';
  const items: string[] = [];

  if (mission.spatialResolutionCm != null) {
    items.push(`Resolution: ${mission.spatialResolutionCm} cm/pixel`);
  }
  if (mission.capturePeriod) {
    items.push(`Survey: ${mission.capturePeriod}`);
  }

  if (items.length === 0) return null;

  return (
    <div className={`mt-2 space-y-0.5 text-[10px] ${muted}`}>
      {items.map((line) => (
        <p key={line}>{line}</p>
      ))}
    </div>
  );
}

type HistogramChartData = {
  width: number;
  height: number;
  points: Array<{ x: number; y: number; value: number; count: number; color: string }>;
  linePath: string;
  areaPath: string;
  padL: number;
  padT: number;
  innerH: number;
  padR: number;
  binStep: number;
};

function DroneHistogramChart({
  chart,
  isDark,
  rangeMin,
  rangeMax,
}: {
  chart: HistogramChartData;
  isDark: boolean;
  rangeMin: number;
  rangeMax: number;
}) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const hovered = hoveredIndex != null ? chart.points[hoveredIndex] : null;
  const tooltipLeftPct = hovered ? (hovered.x / chart.width) * 100 : 0;
  const tooltipFlip = tooltipLeftPct > 72;

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${chart.width} ${chart.height}`}
        className="w-full touch-none"
        role="img"
        aria-label="Crop condition distribution"
        onMouseLeave={() => setHoveredIndex(null)}
      >
        <defs>
          <linearGradient id="drone-hist-grad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#d73027" />
            <stop offset="33%" stopColor="#fdae61" />
            <stop offset="66%" stopColor="#fee08b" />
            <stop offset="100%" stopColor="#1a9850" />
          </linearGradient>
        </defs>
        <line
          x1={chart.padL}
          y1={chart.padT + chart.innerH}
          x2={chart.width - chart.padR}
          y2={chart.padT + chart.innerH}
          stroke={isDark ? '#4b5563' : '#d1d5db'}
          strokeWidth="1"
        />
        <path d={chart.areaPath} fill="url(#drone-hist-grad)" opacity="0.35" />
        <path
          d={chart.linePath}
          fill="none"
          stroke={isDark ? '#34d399' : '#059669'}
          strokeWidth="1.5"
        />
        {hovered && (
          <line
            x1={hovered.x}
            y1={chart.padT}
            x2={hovered.x}
            y2={chart.padT + chart.innerH}
            stroke={isDark ? '#6ee7b7' : '#059669'}
            strokeWidth="1"
            strokeDasharray="3 2"
            opacity="0.85"
            pointerEvents="none"
          />
        )}
        {chart.points.map((p, i) => {
          const halfStep = chart.binStep / 2;
          const rectX = Math.max(chart.padL, p.x - halfStep);
          const rectW = Math.min(chart.width - chart.padR - rectX, chart.binStep);
          const isActive = hoveredIndex === i;
          return (
            <g key={i}>
              <rect
                x={rectX}
                y={chart.padT}
                width={rectW}
                height={chart.innerH}
                fill="transparent"
                className="cursor-crosshair"
                onMouseEnter={() => setHoveredIndex(i)}
                onFocus={() => setHoveredIndex(i)}
                onBlur={() => setHoveredIndex(null)}
                tabIndex={0}
                role="presentation"
              />
              <circle
                cx={p.x}
                cy={p.y}
                r={isActive ? 3.5 : 1.5}
                fill={p.color}
                stroke={isActive ? (isDark ? '#ecfdf5' : '#ffffff') : 'none'}
                strokeWidth={isActive ? 1 : 0}
                pointerEvents="none"
              />
            </g>
          );
        })}
        <text x={chart.padL} y={chart.height - 6} fontSize="9" fill={isDark ? '#9ca3af' : '#6b7280'}>
          {rangeMin.toFixed(2)}
        </text>
        <text
          x={chart.width - chart.padR}
          y={chart.height - 6}
          textAnchor="end"
          fontSize="9"
          fill={isDark ? '#9ca3af' : '#6b7280'}
        >
          {rangeMax.toFixed(2)}
        </text>
      </svg>

      {hovered && (
        <div
          className={`pointer-events-none absolute top-1 z-10 min-w-[7.5rem] rounded-md border px-2 py-1.5 text-[10px] shadow-md ${
            isDark
              ? 'border-gray-600 bg-gray-900/95 text-gray-100'
              : 'border-gray-200 bg-white/95 text-gray-800'
          }`}
          style={{
            left: `${tooltipLeftPct}%`,
            transform: tooltipFlip ? 'translateX(-100%)' : 'translateX(-50%)',
          }}
        >
          <p className="font-semibold tabular-nums">Yield: {fmtNum(ndviToEstimatedYieldTonsHa(hovered.value), 2)} t/ha</p>
          <p className={`tabular-nums ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Condition: {fmtNum(hovered.value)}
          </p>
        </div>
      )}
    </div>
  );
}

export default function DronePrescriptionPanel({
  state,
  mission,
  usingMissionExtent,
  variant = 'inline',
  onClose,
}: DronePrescriptionPanelProps) {
  const { isDark } = useTheme();
  const isOverlay = variant === 'overlay';
  const [rangeMin, rangeMax] = DRONE_NDVI_RANGE;

  const histogramChart = useMemo(() => {
    if (state.status !== 'loaded' || state.data.histogram.length === 0) return null;

    const bins = state.data.histogram;
    const width = isOverlay ? 340 : 320;
    const height = isOverlay ? 130 : 110;
    const padL = 32;
    const padR = 12;
    const padT = 12;
    const padB = 28;
    const innerW = width - padL - padR;
    const innerH = height - padT - padB;

    const maxCount = Math.max(...bins.map((b) => b.count), 1);

    const points = bins.map((bin, i) => {
      const x = padL + (i / Math.max(bins.length - 1, 1)) * innerW;
      const y = padT + innerH - (bin.count / maxCount) * innerH;
      return { x, y, ...bin, color: classColorForNdvi(bin.value) };
    });

    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
    const areaPath =
      `${linePath} L${points[points.length - 1].x.toFixed(1)},${(padT + innerH).toFixed(1)} ` +
      `L${points[0].x.toFixed(1)},${(padT + innerH).toFixed(1)} Z`;
    const binStep = bins.length > 1 ? innerW / (bins.length - 1) : innerW;

    return { width, height, points, linePath, areaPath, padL, padT, innerH, padR, binStep };
  }, [state, isOverlay]);

  if (!mission || state.status === 'idle') return null;

  const shell = isOverlay
    ? `rounded-xl border shadow-2xl backdrop-blur-sm ${
        isDark
          ? 'border-gray-600/80 bg-gray-900/95 text-gray-100'
          : 'border-gray-200/90 bg-white/95 text-gray-900'
      }`
    : `rounded-xl border ${isDark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-white'}`;

  if (state.status === 'loading') {
    return (
      <div className={`${shell} ${isOverlay ? 'p-4' : 'mt-2 p-3.5'}`}>
        <div className="mb-3 flex items-center justify-between gap-2">
          <div>
            <h3 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Drone Analysis
            </h3>
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{mission.label}</p>
          </div>
          {onClose && (
            <button type="button" onClick={onClose} className="rounded p-1 hover:bg-black/5" aria-label="Close">
              <i className="ri-close-line text-lg" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div className="h-full w-1/3 animate-pulse rounded-full bg-emerald-500" />
          </div>
          <span className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Loading results…
          </span>
        </div>
      </div>
    );
  }

  if (state.status === 'unavailable') {
    return (
      <div className={`${shell} ${isOverlay ? 'p-4' : 'mt-2 p-3.5'}`}>
        <p className="text-xs text-gray-500">
          No vegetation pixels in the analysis area for {mission.label}.
        </p>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className={`${shell} ${isOverlay ? 'p-4' : 'mt-2 p-3.5'}`}>
        <p className="text-xs text-red-500">{state.message}</p>
      </div>
    );
  }

  const { data } = state;

  return (
    <div className={`${shell} ${isOverlay ? 'p-4' : 'mt-2 p-3.5'}`}>
      {/* Header */}
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <h3 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Drone Analysis
          </h3>
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{mission.label}</p>
        </div>
        {onClose && (
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-black/5" aria-label="Close">
            <i className="ri-close-line text-lg" />
          </button>
        )}
      </div>

      <div className={`mb-3 rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-100 bg-gray-50'}`}>
        <p className="mb-2 text-xs font-semibold">Crop condition</p>
        <div className="space-y-1.5">
          {DRONE_VEGETATION_CLASSES.map((cls, idx) => {
            const midYield = ndviToEstimatedYieldTonsHa(vegetationClassMidpointNdvi(cls));
            return (
            <div key={cls.id} className="flex items-center gap-2 text-[11px]">
              <span
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[9px] font-bold text-white"
                style={{ backgroundColor: cls.color }}
              >
                {idx + 1}
              </span>
              <span className={`flex-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{cls.label}</span>
              <span className={`shrink-0 tabular-nums ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {fmtNum(midYield, 1)} t/ha
              </span>
            </div>
            );
          })}
        </div>
      </div>

      {/* Histogram */}
      {histogramChart && (
        <div className="mb-3">
          <p className="mb-1.5 text-xs font-semibold">Condition distribution</p>
          <DroneHistogramChart
            chart={histogramChart}
            isDark={isDark}
            rangeMin={rangeMin}
            rangeMax={rangeMax}
          />
        </div>
      )}

      {/* Class summary */}
      <div className="mb-3 overflow-x-auto">
        <p className="mb-1.5 text-xs font-semibold">Summary by zone</p>
        <table className="w-full border-collapse text-[11px]">
          <thead>
            <tr className={isDark ? 'text-gray-400' : 'text-gray-500'}>
              <th className="pb-1.5 text-left font-medium">Zone</th>
              <th className="pb-1.5 text-right font-medium">Mean</th>
              <th className="pb-1.5 text-right font-medium">Area (ha)</th>
              <th className="pb-1.5 text-right font-medium">Yield (t/ha)</th>
              <th className="pb-1.5 text-right font-medium">Production (t)</th>
            </tr>
          </thead>
          <tbody>
            {data.classes.map((cls, idx) => (
              <tr key={cls.id} className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                <td className="py-1.5">
                  <span className="inline-flex items-center gap-1.5">
                    <span
                      className="inline-flex h-4 w-4 items-center justify-center rounded text-[8px] font-bold text-white"
                      style={{ backgroundColor: cls.color }}
                    >
                      {idx + 1}
                    </span>
                    <span>{cls.label}</span>
                  </span>
                </td>
                <td className="py-1.5 text-right tabular-nums">{fmtNum(cls.zonalMean)}</td>
                <td className="py-1.5 text-right tabular-nums">{fmtHa(cls.areaHa)}</td>
                <td className="py-1.5 text-right tabular-nums text-gray-500">{fmtNum(cls.estYieldTonsHa, 2)}</td>
                <td className="py-1.5 text-right tabular-nums">{fmtNum(cls.productionTons, 2)} t</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div
        className={`grid grid-cols-3 gap-2 rounded-lg border px-3 py-2.5 ${
          isDark ? 'border-emerald-800/50 bg-emerald-950/30' : 'border-emerald-100 bg-emerald-50/80'
        }`}
      >
        <div>
          <span className="block text-[10px] uppercase tracking-wide text-gray-500">Total area</span>
          <span className="text-base font-bold tabular-nums text-emerald-700 dark:text-emerald-400">
            {fmtHa(data.totalAreaHa)} <span className="text-xs font-semibold">ha</span>
          </span>
        </div>
        <div className="text-center">
          <span className="block text-[10px] uppercase tracking-wide text-gray-500">Mean yield</span>
          <span className="text-base font-bold tabular-nums text-emerald-700 dark:text-emerald-400">
            {fmtNum(data.overallMeanYieldTonsHa, 2)} <span className="text-xs font-semibold">t/ha</span>
          </span>
        </div>
        <div className="text-right">
          <span className="block text-[10px] uppercase tracking-wide text-gray-500">Total production</span>
          <span className="text-base font-bold tabular-nums text-emerald-700 dark:text-emerald-400">
            {fmtNum(data.totalProductionTons, 2)} <span className="text-xs font-semibold">t</span>
          </span>
        </div>
      </div>

      <DroneMetadataFooter mission={mission} isDark={isDark} />
    </div>
  );
}

export { type DronePrescriptionState };
